import { __decorate } from "tslib";
// NPM
import { Component, Mixins } from "vue-property-decorator";
import { interpret } from "xstate";
import { Guid } from "guid-typescript";
// S1
import BaseMixer from "@/router/views/BaseMixer";
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header.vue";
import SectionLoader from "@/components/widgets/SectionLoader.vue";
import DeleteComponent from "@/components/deleteComponent.vue";
import ClientUpdatesModule from "@/store/modules/clientUpdates.module";
import updatesMachine from "./machines/UpdatesMachine";
import UpdatesTimeline from "./UpdatesTimeline.vue";
import UpdatesFilter from "./components/UpdatesFilter.vue";
import AddUpdateModal from "./AddUpdateModal.vue";
import AddActionItemModal from "./actionItems/AddActionItemModal.vue";
import { actionItemMatches, DEACTIVATED_CLIENT_HINT, DEACTIVATED_TEAM_ACTION_ITEM_HINT, DEACTIVATED_TEAM_HINT, SELECT_TEAM_HINT, monthsAgo, NOTES_LABEL, sameId, textMatches, toCalendarDay, UPDATE_SIDE_CLIENT_ID, UPDATE_SIDE_DEVELOPMENT_ID, updateText, } from "./updatesHelpers";
let Updates = /** @class */ (() => {
    let Updates = class Updates extends Mixins(BaseMixer) {
        constructor() {
            super(...arguments);
            // Fresh default date range on every visit (the machine's own defaults are fixed at import time)
            this.machine = updatesMachine.withContext({
                ...updatesMachine.context,
                dateFrom: monthsAgo(3),
                dateTo: new Date(),
            });
            this.machineService = interpret(this.machine, { devTools: true });
            this.state = this.machine.initialState;
            this.deactivatedHint = DEACTIVATED_CLIENT_HINT;
            this.title = "Updates";
            this.items = [
                {
                    text: "Home",
                    to: "/dashBoard",
                },
                {
                    text: "Client Success",
                },
                {
                    text: "Updates",
                    active: true,
                },
            ];
            this.tabs = [
                { key: "all", label: "All Updates" },
                { key: "client", label: "Client" },
                { key: "development", label: "Team" },
                { key: "actionItems", label: "Action Items" },
            ];
            // Updates screen filters
            this.updatesClientId = "";
            this.searchText = "";
            // Client the page selected on load. Clear Filter returns to it.
            this.defaultClientId = "";
            // That client's only active team, picked on load (empty when it has none or several). Clear Filter returns to it.
            this.defaultTeamId = "";
            // Modal / confirm targets
            this.editingEntry = null;
            this.updateModalSideId = UPDATE_SIDE_CLIENT_ID;
            this.pendingDeleteUpdateId = null;
            // First load: page data, then the first timeline for the auto-selected client
            this.initialLoadDone = false;
        }
        send(event) {
            this.machineService.send(event);
        }
        // Store
        get clientOptions() {
            return ClientUpdatesModule.clientOptions;
        }
        get interactionTypes() {
            return (ClientUpdatesModule.interactionTypes || []).map((x) => ({ id: x.id.toString().toLowerCase(), description: x.value }));
        }
        get timeline() {
            return ClientUpdatesModule.timeline;
        }
        // The selected client's teams - they load when the client is picked, before any updates
        get teams() {
            return ClientUpdatesModule.teams;
        }
        get tabCounts() {
            return ClientUpdatesModule.tabCounts;
        }
        // View state
        get activeTab() {
            return this.state.context.activeTab;
        }
        get updatesClientName() {
            return this.clientNameOf(this.updatesClientId);
        }
        // Deactivated client: its updates and action items can be viewed, not added or changed
        get isClientReadOnly() {
            const client = (this.clientOptions || []).find((c) => sameId(c.id, this.updatesClientId));
            return !!client && client.isActive === false;
        }
        get canAddToClient() {
            return !!this.updatesClientId && !this.isClientReadOnly;
        }
        // The team picked in the Team filter (null = "Please select") - a new update goes to it, as it goes to the client
        get filterTeam() {
            return (this.teams || []).find((t) => sameId(t.id, this.state.context.teamId)) || null;
        }
        // Deactivated team: like a deactivated client, its updates can be viewed but no new ones added
        get isTeamReadOnly() {
            return !!this.filterTeam && this.filterTeam.isActive === false;
        }
        // A new update goes to the filter's team, so Add Update needs an active team picked
        get canAddUpdate() {
            return this.canAddToClient && !!this.filterTeam && !this.isTeamReadOnly;
        }
        get addUpdateHint() {
            if (this.isClientReadOnly) {
                return DEACTIVATED_CLIENT_HINT;
            }
            if (!this.filterTeam) {
                return SELECT_TEAM_HINT;
            }
            return this.isTeamReadOnly ? DEACTIVATED_TEAM_HINT : "";
        }
        // A new action item takes the filter's team when one is picked (optional), so only a deactivated team blocks it
        get canAddActionItem() {
            return this.canAddToClient && !this.isTeamReadOnly;
        }
        get addActionItemHint() {
            if (this.isClientReadOnly) {
                return DEACTIVATED_CLIENT_HINT;
            }
            return this.isTeamReadOnly ? DEACTIVATED_TEAM_ACTION_ITEM_HINT : "";
        }
        get filterTeamId() {
            return this.filterTeam ? this.filterTeam.id.toString() : "";
        }
        get filterTeamName() {
            return this.filterTeam ? this.filterTeam.value : "";
        }
        // Add: the filter's team. Edit: the update keeps its own team.
        get updateModalTeamId() {
            if (this.editingEntry) {
                return this.editingEntry.teamId ? this.editingEntry.teamId.toString() : "";
            }
            return this.filterTeam ? this.filterTeam.id.toString() : "";
        }
        get updateModalTeamName() {
            if (this.editingEntry) {
                return this.editingEntry.teamName || "";
            }
            return this.filterTeam ? this.filterTeam.value : "";
        }
        // Earliest selectable day for update dates and the Custom range (null = no Start Date, no lower bound)
        get updatesClientStartDate() {
            const client = (this.clientOptions || []).find((c) => sameId(c.id, this.updatesClientId));
            return client ? toCalendarDay(client.startDate) : null;
        }
        get isSearching() {
            return this.searchText.trim() !== "";
        }
        // Every tab filters the loaded timeline client-side
        get tabEntries() {
            const entries = this.timeline || [];
            if (this.activeTab === "actionItems") {
                return entries.filter((x) => this.hasActionItems(x));
            }
            if (this.activeTab === "client") {
                return entries.filter((x) => sameId(x.updateSideId, UPDATE_SIDE_CLIENT_ID));
            }
            if (this.activeTab === "development") {
                return entries.filter((x) => sameId(x.updateSideId, UPDATE_SIDE_DEVELOPMENT_ID));
            }
            return entries;
        }
        get visibleEntries() {
            if (!this.isSearching) {
                return this.tabEntries;
            }
            const term = this.searchText;
            return this.tabEntries.filter((x) => [updateText(x.updateHtml), x.interactionTypeLabel || NOTES_LABEL, x.createdByName, x.updateSideLabel].some((value) => textMatches(value, term)) ||
                (x.actionItems || []).some((item) => actionItemMatches(item, term)));
        }
        // A client's teams load before its timeline - the section loader covers both
        get isLoadingTimeline() {
            return this.state.matches("timelineInfo.gettingTeams") || this.state.matches("timelineInfo.gettingTimeline");
        }
        // A client is picked but no team yet: its updates aren't loaded until one is
        get needsTeam() {
            return !!this.updatesClientId && !this.state.context.teamId && !this.state.matches("timelineInfo.gettingTeams");
        }
        get isSavingActionItemStatus() {
            return this.state.matches("actionItemStatusInfo.savingActionItemStatus");
        }
        get showUpdateModal() {
            return this.state.matches("updateInfo.updateFormOpen") || this.state.matches("updateInfo.savingUpdate");
        }
        get showActionItemModal() {
            return this.state.matches("actionItemInfo.actionItemFormOpen") || this.state.matches("actionItemInfo.savingActionItem");
        }
        // The Action Items count matches what that tab lists: loaded updates that have action items
        tabCount(tab) {
            if (tab === "actionItems") {
                return (this.timeline || []).filter((x) => this.hasActionItems(x)).length;
            }
            const counts = this.tabCounts || { all: 0, client: 0, development: 0, actionItems: 0 };
            return counts[tab] || 0;
        }
        hasActionItems(entry) {
            return entry.hasActionItems || (entry.actionItems || []).length > 0;
        }
        clientNameOf(id) {
            const client = (this.clientOptions || []).find((c) => sameId(c.id, id));
            return client ? client.value : "";
        }
        toGuid(id) {
            return id ? Guid.parse(id) : null;
        }
        // Tabs
        selectTab(tab) {
            if (tab === this.activeTab) {
                return;
            }
            this.send({ type: "SELECT_TAB", tab: tab });
        }
        // Filter sidebar
        onClientChange(id) {
            this.updatesClientId = id;
            // Drop the previous client's teams so they never show under the new client while its timeline loads
            ClientUpdatesModule.SetTeams([]);
            this.send({ type: "SELECT_CLIENT", clientId: this.toGuid(id) });
        }
        // Only that team's updates (server-side, so the tab counts match); null lists every update
        onTeamChange(teamId) {
            this.send({ type: "CHANGE_TEAM", teamId: teamId });
        }
        onDateRangeChange(range) {
            this.send({ type: "CHANGE_DATE_RANGE", dateFrom: range.dateFrom, dateTo: range.dateTo });
        }
        // Update modal
        openAddUpdate() {
            if (!this.canAddUpdate) {
                return;
            }
            this.editingEntry = null;
            this.updateModalSideId = this.activeTab === "development" ? UPDATE_SIDE_DEVELOPMENT_ID : UPDATE_SIDE_CLIENT_ID;
            this.send({ type: "ADD_UPDATE" });
        }
        openEditUpdate(entry) {
            this.editingEntry = entry;
            this.send({ type: "EDIT_UPDATE" });
        }
        saveUpdate(payload) {
            this.send({
                type: "SAVE_UPDATE",
                payLoad: payload.payLoad,
                actionItems: payload.actionItems,
                links: payload.links,
                files: payload.files,
                editedActionItems: payload.editedActionItems,
                deletedActionItemIds: payload.deletedActionItemIds,
                deletedAttachmentIds: payload.deletedAttachmentIds,
                completedActionItemIds: payload.completedActionItemIds,
                reopenedActionItemIds: payload.reopenedActionItemIds,
                closedActionItemIds: payload.closedActionItemIds,
                closeAfter: payload.closeAfter,
            });
        }
        // Completed tick on a timeline action item
        setActionItemStatus(payload) {
            this.send({ type: "SET_ACTION_ITEM_STATUS", actionItemId: payload.actionItemId, completed: payload.completed });
        }
        closeUpdateModal() {
            this.send({ type: "CLOSE_MODAL" });
        }
        requestDeleteUpdate(entry) {
            this.pendingDeleteUpdateId = entry.id.toString();
            this.send({ type: "DELETE_UPDATE" });
        }
        confirmDeleteUpdate() {
            this.send({ type: "DELETING_UPDATE", payLoad: this.pendingDeleteUpdateId });
        }
        cancelDeleteUpdate() {
            this.pendingDeleteUpdateId = null;
            this.send({ type: "CLOSE_MODAL" });
        }
        // Action Item modal
        openAddActionItem() {
            if (!this.canAddActionItem) {
                return;
            }
            this.send({ type: "ADD_ACTION_ITEM" });
        }
        saveActionItem(model) {
            this.send({ type: "SAVE_ACTION_ITEM", payLoad: model });
        }
        closeActionItemModal() {
            this.send({ type: "CLOSE_ACTION_ITEM_MODAL" });
        }
        created() {
            // Start clean - a late response from a previous visit may have written to the store after it closed
            ClientUpdatesModule.ResetState();
            // Opened for a client (Client listing / Weekly Standup link): that client is selected instead of the first one
            const requestedClientId = this.$route.query.clientId;
            if (typeof requestedClientId === "string" && requestedClientId) {
                this.machine = this.machine.withContext({ ...this.machine.context, requestedClientId: requestedClientId });
                this.machineService = interpret(this.machine, { devTools: true });
                this.state = this.machine.initialState;
            }
            this.machineService.onTransition((state) => {
                this.state = state;
                // After a Save that keeps the modal open, it goes on editing the saved update
                if (state.context.savedUpdate && state.context.savedUpdate !== this.editingEntry) {
                    this.editingEntry = state.context.savedUpdate;
                }
                // The machine auto-selects the first client once the page data arrives
                if (!this.updatesClientId && state.context.clientId) {
                    this.updatesClientId = state.context.clientId.toString();
                    if (!this.defaultClientId) {
                        this.defaultClientId = this.updatesClientId;
                    }
                }
                // The page loader stays up until the page data and the first timeline have both finished
                if (!this.initialLoadDone && !state.matches("pageDataInfo.gettingPageData") && !state.matches("timelineInfo.gettingTeams") && !state.matches("timelineInfo.gettingTimeline")) {
                    this.initialLoadDone = true;
                    // The machine has picked the first client's team by now when it has only one (its teams load first, then that team's timeline)
                    this.defaultTeamId = state.context.teamId || "";
                }
            });
            this.machineService.start();
        }
        beforeDestroy() {
            this.machineService.stop();
            ClientUpdatesModule.ResetState();
        }
    };
    Updates = __decorate([
        Component({
            name: "Updates",
            components: {
                Layout,
                PageHeader,
                SectionLoader,
                DeleteComponent,
                UpdatesFilter,
                UpdatesTimeline,
                AddUpdateModal,
                AddActionItemModal,
            },
        })
    ], Updates);
    return Updates;
})();
export default Updates;
